import {useState, useEffect} from "react";
import {sendEventToGA} from "./../utils.js";
import {getSolrListFacetCountAsObject} from "./searchresultsfacet_utils.js";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {library as fontAwesomeLibrary} from "@fortawesome/fontawesome-svg-core";
import {faCaretDown} from "@fortawesome/free-solid-svg-icons";
import {CollapsibleFacet} from "./collapsiblefacet";
import {SearchResultCloseablePillList} from "./searchresultcloseablepilllist.js";
import {DateHistogramTool} from "./datehistogramtool.js";
import {
  Box,
  Button,
} from "@mui/material";

const MAXIMUM_OPEN_FACET = 7;

/**
 * Displays the "Refine Search" facet panel o++n the search results parge.
 *
 * @param facetData Object holding facet response from backend. Includes counts
 * for each facet.
 * @param facetRestricts The current set of facet restrictions
 * @param skipMissionsFacet Don't display the missions and observatories facet.
 * @param skipRegionsFacet Don't display the regions facet.
 * @param currentQuery Query from user(current page).
 * @param forceAlphaSort Force the facets to be sorted by alphabetical order.
 * @param customTitle Custom title for the facets bar.
 * @param navigate Function used to move to different react page
 * @param getNavigateUrl Function on Query String (restrictions) that returns the
 * URL to navigate to when a new facet is selected.
 * @param regionTaxonomyTitles Titles of each region taxonomy node.
 */
export function SearchResultsFacet(props) {
    const {facetData,
           facetRestricts,
           defaultFacetData,
           instruments,
           measurements,
           eras,
           timeResolutions,
           regions,
           numFound,
           skipMissionsFacet,
           skipRegionsFacet,
           forceAlphaSort,
           customTitle,
           navigate,
           query,
           getNavigateUrl,
           setDateRange,
           regionTaxonomyTitles} = props;
    const [openCollapsibleFacets, setOpenCollapsibleFacets] = useState(["mission_facet_cs"]);
    const [facetRestrictsPillList, setFacetRestrictsPillList] = useState([]);
    const [facetRes, setFacetRes] = useState(facetRestricts || {});
    const [histogramRange, setHistogramRange] = useState(null);

    useEffect(() => {

        const newPills = [];

        Object.entries(facetRestricts).forEach(([key, value]) => {
            if(typeof(value) === "string") {
                    value.split(",").forEach((val, idx) => {
                        newPills.push({
                        id: `${key}-${idx}`,
                        label: val,
                        variant: "primary",
                        size: "md",
                        fk: `${key}`
                    });
                });
            } else if(typeof(value) === "boolean") {
                newPills.push({
                    id: `${key}-${value}`,
                    label: key,
                    variant: "warning",
                    size: "md",
                    fk: `${key}`
                })
            }
        });
        setFacetRestrictsPillList(newPills);
    }, [facetRestricts]);

    if (numFound === 0 && facetRestricts === "" ) {
        return <div />;
    }

    const updateHistogramRange = (ranges) => {
      if (!ranges || ranges.length === 0) {
        setHistogramRange(null);
        setDateRange(null);
        return;
      }
      setHistogramRange(ranges);
      setDateRange(ranges);
    };

    const resetData = () => {
        navigate(`/search/${encodeURIComponent(query?.trim())}/1`);
    }

    const sampleData = Array.from({ length: 300 }, () => ({
        date: new Date(2010 + Math.random() * 15, Math.floor(Math.random() * 12),1
        ).toISOString(),
    }));

    const onPillClose = (pillKey, val) => {
        const newPills = [];
        let newFR = {...facetRestricts};

        if(typeof(newFR[pillKey]) === "string") {
            newFR[pillKey] = newFR[pillKey].split(",").filter(item => item !== val).join(",");
        } else if(typeof(newFR[pillKey]) === "boolean") {
            delete newFR[pillKey];
        }

        if(newFR[pillKey] === "" || newFR[pillKey] === null) {
            delete newFR[pillKey];
        }
        setFacetRes(newFR);

        const params = new URLSearchParams();

        // Handle each restriction properly
        Object.entries(newFR).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                params.append(key, value.join(","));
            } else if (typeof value === 'string' && value.includes(',')) {
                params.append(key, value)
            } else {
                params.append(key, value);
            }
        });

        /*const histoParams = new URLSearchParams();
        if(histogramRange) {
            histoParams.append('nominal_start_time_from', histogramRange.start);
            histoParams.append('nominal_start_time_to', histogramRange.end);
        }*/

        const histoParams = new URLSearchParams();
        if (histogramRange && histogramRange.length > 0) {
            histoParams.append(
            "nominal_start_time_ranges",
            histogramRange.map((r) => `${r.from}/${r.to}`).join(",")
            );
        }

        navigate(getNavigateUrl(params.toString(), histoParams.toString()));
    };

    const onClickMaker = (newKey, newValue) => {
        let newRestrictions = {...facetRestricts};


        if(newKey === "mission_facet_cs") {
            if(!newRestrictions[newKey]){
                newRestrictions = {};
            }
        }

        if (newValue === null) {
            if(newKey==="access_method"){
                delete newRestrictions["has_https"];
                delete newRestrictions["has_ftp"];
                delete newRestrictions["has_heliocloud_s3"];
                delete newRestrictions["has_api"];
            }
            if(newKey==="access_method"){
                newRestrictions[newKey] = {};
            }
            delete newRestrictions[newKey];
        } else {

            const currentValues = Array.isArray(newRestrictions[newKey])
                                  ? newRestrictions[newKey]
                                  : newRestrictions[newKey] !== undefined
                                      ? [newRestrictions[newKey]]
                                      : [];

            if(currentValues.length === 0){
                // add the value
                newRestrictions[newKey] = newValue;
            } else if (currentValues[0].split(",").includes(newValue)) {

                const updatedValues = currentValues[0].split(",").filter(v => v !== newValue);
                if (updatedValues.length > 0) {
                    newRestrictions[newKey] = updatedValues.join(",");
                } else {
                    delete newRestrictions[newKey];
                }
            } else {
                newRestrictions[newKey] = currentValues + ',' + newValue;
            }
        }
        const params = new URLSearchParams();

        // Handle each restriction properly
        Object.entries(newRestrictions).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                params.append(key, value.join(","));
            } else if (typeof value === 'string' && value.includes(',')) {
                params.append(key, value)
            } else {
                params.append(key, value);
            }
        });

        setFacetRes(newRestrictions);

        /*const histoParams = new URLSearchParams();

        if(histogramRange) {
            histoParams.append('nominal_start_time_from', histogramRange.start);
            histoParams.append('nominal_start_time_to', histogramRange.end);
        }*/

        const histoParams = new URLSearchParams();
            if (histogramRange && histogramRange.length > 0) {
                histoParams.append(
                    "nominal_start_time_ranges",
                    histogramRange.map((r) => `${r.from}/${r.to}`).join(",")
                );
            }
        navigate(getNavigateUrl(params.toString(), histoParams.toString()));
    };

    const toggleFacet = (facetKey) => {
        setOpenCollapsibleFacets(prevOpenCollapsibleFacets => {
            if (prevOpenCollapsibleFacets.includes(facetKey)) {
                return prevOpenCollapsibleFacets.filter(f => f !== facetKey);
            } else if (prevOpenCollapsibleFacets.length < MAXIMUM_OPEN_FACET) {
                return [...prevOpenCollapsibleFacets, facetKey];
            } else {
                onClickMaker(prevOpenCollapsibleFacets[0], null);
                const tempArr = prevOpenCollapsibleFacets.slice(1,MAXIMUM_OPEN_FACET);
                return [...tempArr, facetKey];
            }
        });
    };

    return (
        <div className="searchResultsFacet">
          {query && <DateHistogramTool query={query} numFound={numFound} facetRestricts={facetRestricts}
            getNavigateUrl={getNavigateUrl} updateHistogramRange={updateHistogramRange}/>}
          {facetRestrictsPillList.length > 0
            &&
            <SearchResultCloseablePillList
              pills={facetRestrictsPillList}
              onPillClose= {onPillClose} />
          }

          <div>
            {
               skipMissionsFacet
               ?
               ""
               :
               <CollapsibleFacet title={"Mission and Observatories"}
                                 isOpen={openCollapsibleFacets.includes('mission_facet_cs')}
                                 facetKey={"mission_facet_cs"}
                                 toggleOpen={() => {toggleFacet("mission_facet_cs")}}>
                 <ExpandableFacets
                   title={""}
                   facetKey={"mission_facet_cs"}
                   facetData={facetData}
                   defaultFacetData={defaultFacetData}
                   facetRestricts={facetRestricts}
                   onClickMaker={onClickMaker}
                   sortOrder={forceAlphaSort ? "alpha" : "counts"}
                 />
               </CollapsibleFacet>
            }

            <CollapsibleFacet title="Instruments and Data Sources"
                                 isOpen={openCollapsibleFacets.includes('instrument_facet_cs')}
                                 facetKey={"instrument_facet_cs"}
                                 toggleOpen={() => {toggleFacet("instrument_facet_cs")}}>
              <ExpandableFacets
                title={"Instruments and Data Sources"}
                facetKey={"instrument_facet_cs"}
                facetData={facetData}
                defaultFacetData={defaultFacetData}
                instruments={instruments}
                facetRestricts={facetRestricts}
                onClickMaker={onClickMaker}
                sortOrder={forceAlphaSort ? "alpha" : "counts"}
              />
            </CollapsibleFacet>

            <CollapsibleFacet title="Measurement Type"
                                 isOpen={openCollapsibleFacets.includes('measurement_type_cs')}
                                 facetKey={"measurement_type_cs"}
                                 toggleOpen={() => {toggleFacet("measurement_type_cs")}}>
              <MeasurementTypeFacets
                facetData={facetData}
                facetRestricts={facetRestricts}
                defaultFacetData={defaultFacetData}
                measurements={measurements}
                onClickMaker={onClickMaker} />
            </CollapsibleFacet>

            {
              skipRegionsFacet
              ?
              ""
              :
              <CollapsibleFacet title="Regions"
                                 isOpen={openCollapsibleFacets.includes('regions')}
                                 facetKey={"regions"}
                                 toggleOpen={() => {toggleFacet("regions")}}>
                <RegionFacets
                  title={"Regions"}
                  facetKey={"region"}
                  facetData={facetData}
                  regions={regions}
                  facetRestricts={facetRestricts}
                  onClickMaker={onClickMaker}
                  regionTaxonomyTitles={regionTaxonomyTitles}
                />
              </CollapsibleFacet>
            }

            <CollapsibleFacet title="Time Resolution"
                                 isOpen={openCollapsibleFacets.includes('cadence_cat')}
                                 facetKey={"cadence_cat"}
                                 toggleOpen={()=>{toggleFacet("cadence_cat")}}>
              <TimeResolutionFacets
                facetData={facetData}
                facetRestricts={facetRestricts}
                timeResolutions={timeResolutions}
                onClickMaker={onClickMaker} />
            </CollapsibleFacet>

            <CollapsibleFacet title="Era"
                                 isOpen={openCollapsibleFacets.includes('eras')}
                                 facetKey={"eras"}
                                 toggleOpen={()=>{toggleFacet("eras")}}>
              <EraFacets
                facetData={facetData}
                facetRestricts={facetRestricts}
                eras={eras}
                onClickMaker={onClickMaker} />
            </CollapsibleFacet>

            <CollapsibleFacet title="Access Method"
                                 isOpen={openCollapsibleFacets.includes('access_method')}
                                 facetKey={"access_method"}
                                 toggleOpen={()=>{toggleFacet("access_method")}}>
              <AccessMethodFacets
                facetData={facetData}
                facetRestricts={facetRestricts}
                onClickMaker={onClickMaker} />
            </CollapsibleFacet>
          </div>
          {numFound !== 0 && <div className="searchResultFacetResetContainer">
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={resetData}
                disabled={false}
              >
                Reset
              </Button>
            </Box>
          </div>}
        </div>
    );
}


/**
 * Displays the section of the facet panel pertaining to Regions
 *
 * @param props.facetData Backend information about facets
 * @param props.facetRestricts Current facet restrictions in place on the page
 * @param props.onClickMaker Function to help alter the current facet
 * @param props.regionTaxonomyTitles Titles of each region taxonomy node
 * restrictions
 */
function RegionFacets(props) {
    const {facetData, facetRestricts, onClickMaker, regions, defaultFacetData, regionTaxonomyTitles} = props;
    const regionFacets = [];
    const counts = getSolrListFacetCountAsObject(facetData?.facet_fields?.region);
    const topLevelKeys = (
        Object
            .keys(counts)
            .filter(item => !item.includes("."))    // top level only
            .filter(item => !item.startsWith("+"))  // special item for node name
    );
    const defaultNumberToShow = 30;
    const [regionVisibleCount, setRegionVisibleCount] = useState(defaultNumberToShow);

    let facetUnchecked = [];
    let uncheckedFacetsJSX = [];


    // Group keys in top buckets of their top level key. For example,
    // {sun: ["sun.chromosphere", "sun.photosphere", ...], ...}
    const topLevelBucket = {};
    Object.keys(counts).forEach(key => {
        if (!key.includes(".")) {
            return;
        }
	const topLevelKey = key.split(".")[0];
	if (!topLevelBucket[topLevelKey]) {
	    topLevelBucket[topLevelKey] = [];
	}
	topLevelBucket[topLevelKey].push(key);
    });

    // Loop through top level buckets and display indented child regions
    topLevelKeys.forEach(topLevelKey => {
        if (counts[topLevelKey] && counts[topLevelKey] > 0) {
            const topLevelOnClick = (event) => onClickMaker(
                "region",
                facetRestricts.region === topLevelKey ? null : topLevelKey
            );
	    const topLevelTitle = regionTaxonomyTitles[topLevelKey] || topLevelKey;
            const subregionContent = [];
            const bucketItems = topLevelBucket[topLevelKey] || [];

            bucketItems.forEach(subLevelKey => {
                const subLevelOnClick = (event) => onClickMaker(
                    "region",
                    facetRestricts.region === subLevelKey ? null : subLevelKey
                );
                const subLevelName = subLevelKey.split(".").pop();
                const subLevelTitle = regionTaxonomyTitles[subLevelName] || subLevelName;

                if (counts[subLevelKey] === 0) {
                    return;
                }

                subregionContent.push(
                    <div
                      className={`searchResultFacetItem searchResultFacetItemIndented ${ facetRestricts.region===subLevelKey ? "searchResultFacetSelected" : ""}`}
                      onClick={() => { HandleFacetClick("regions", subLevelTitle); subLevelOnClick()}}>
                      <input
                        type="checkbox"
                        checked={facetRestricts.region===subLevelKey} readOnly />
                      <span className="facetLabel" >{subLevelTitle} ({counts[subLevelKey]})</span>
                    </div>
                );
            });

            regionFacets.push(
                <div key={regionFacets.length}>
                  <div
                    className={`searchResultFacetItem ${facetRestricts.region===topLevelKey ? "searchResultFacetSelected" : ""}`}
                    onClick={() => { HandleFacetClick("regions", topLevelTitle); topLevelOnClick()}}>
                    <input
                      type="checkbox"
                      checked={facetRestricts.region===topLevelKey} readOnly />
                    <span className="facetLabel" >{topLevelTitle} ({counts[topLevelKey]})</span>
                  </div>
                  {subregionContent}
                </div>
            );
        }
    });

    // Handle Regions Facet
    if(regions) {
        const counts = getSolrListFacetCountAsObject(regions);
        const topLevelKeys = (
            Object
                .keys(counts)
                .filter(item => !item.includes("."))    // top level only
                .filter(item => !item.startsWith("+"))  // special item for node name
        );


        const topLevelBucket = {};
        Object.keys(counts).forEach(key => {
            if (!key.includes(".")) {
                return;
            }
            const topLevelKey = key.split(".")[0];
            if (!topLevelBucket[topLevelKey]) {
                topLevelBucket[topLevelKey] = [];
            }
            topLevelBucket[topLevelKey].push(key);
        });

        topLevelKeys.forEach(topLevelKey => {
            if (counts[topLevelKey] && counts[topLevelKey] > 0) {
                const topLevelOnClick = (event) => onClickMaker(
                    "region",
                    facetRestricts.region === topLevelKey ? null : topLevelKey
                );
                const topLevelTitle = regionTaxonomyTitles[topLevelKey] || topLevelKey;
                const subregionContent = [];
                const bucketItems = topLevelBucket[topLevelKey] || [];

                const fRArray = facetRestricts["region"]?.split(",");
                let isSelected = false;
                let isMissionRestricted = facetRestricts["mission_facet_cs"] ? true : false;

                bucketItems.forEach(subLevelKey => {
                    const subLevelOnClick = (event) => onClickMaker(
                        "region",
                        facetRestricts.region === subLevelKey ? null : subLevelKey
                    );
                    const subLevelName = subLevelKey.split(".").pop();
                    const subLevelTitle = regionTaxonomyTitles[subLevelName] || subLevelName;

                    isSelected = fRArray?.filter((v,i)=> {
                                       return v.startsWith(subLevelKey)
                                 });

                    if (counts[subLevelKey] === 0) {
                        return;
                    }

                    subregionContent.push(
                        <div
                          className={isSelected?.length > 0
                           ?  `searchResultFacetItemIndented searchResultFacetItem ${facetRestricts.region?.split(",")?.includes(subLevelKey) ? "searchResultFacetSelected" : ""}`
                           : isMissionRestricted
                               ? "searchResultUncheckedFacetItemBlueIndented searchResultUncheckedFacetItemBlue"
                               :"searchResultUncheckedFacetItemIndented searchResultUncheckedFacetItem"}
                          onClick={() => { HandleFacetClick("regions", subLevelTitle); subLevelOnClick()}}>
                          <input
                            type="checkbox"
                            checked={facetRestricts.region ? facetRestricts.region.split(",")?.includes(subLevelKey) : false} />
                          <span className="facetLabel" >{subLevelTitle} ({counts[subLevelKey]})</span>
                        </div>
                    );
                });

                isSelected = fRArray?.filter((v,i)=>
                              {
                                  return v.startsWith(topLevelKey)
                              });
                isMissionRestricted = facetRestricts["mission_facet_cs"] ? true : false;

                uncheckedFacetsJSX.push(
                    <div key={topLevelKey}>
                      <div
                        className={isSelected?.length > 0
                           ?  `searchResultFacetItem ${ facetRestricts.region?.split(",")?.includes(topLevelKey) ? "searchResultFacetSelected" :""}`
                           : isMissionRestricted
                               ? "searchResultUncheckedFacetItemBlue"
                               :"searchResultUncheckedFacetItem"}
                        onClick={() => { HandleFacetClick("regions", topLevelTitle); topLevelOnClick()}}>
                        <input
                          type="checkbox"
                          checked={facetRestricts.region?.split(",")?.includes(topLevelKey)} />
                        <span className="facetLabel" >{topLevelTitle} ({counts[topLevelKey]})</span>
                      </div>
                      {subregionContent}
                    </div>
                );
            }
        });
    }

    let showMoreDisplay = "";

    if(regions && regions.length > 0) {
        showMoreDisplay = regions.length < regionVisibleCount
                              ? defaultFacetData && defaultFacetData['facet_fields']["region"].length < regionVisibleCount
                                  ? "none" : ""
                              : "";
    } else {
        showMoreDisplay = topLevelKeys.length < regionVisibleCount
                              ? defaultFacetData && defaultFacetData['facet_fields']["region"].length < regionVisibleCount
                                  ? "none" : ""
                              : "";
    }

    fontAwesomeLibrary.add(faCaretDown);

    const isEmptyObject = (obj) => {
        return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
    }

    if (regionFacets.length === 0 && uncheckedFacetsJSX.length === 0) {
        return <div/>;
    }

    return (
        <div className="searchResultsFacetSection" key="regions">
           <p>{isEmptyObject(facetRestricts) && regionFacets}</p>
           <p>{!isEmptyObject(facetRestricts) && uncheckedFacetsJSX?.slice(0, regionVisibleCount)}</p>
        </div>
    );
}


/**
 * Displays the section of the facet panel pertaining to Time Resolution.
 *
 * Displays categories such as "minutes", "seconds", etc.
 *
 * @param props.facetData Backend information about facets
 * @param props.facetRestricts Current facet restrictions in place on the page
 * @param props.onClickMaker Function to help alter the current facet
 * restrictions
 */
function TimeResolutionFacets(props) {
    const {facetData, facetRestricts, timeResolutions, onClickMaker} = props;
    const timeResFacets = [];
    const counts = getSolrListFacetCountAsObject(facetData?.facet_fields?.cadence_cat);
    const keys = ["years", "months", "days", "hours", "minutes", "seconds", "milliseconds", "microseconds"];

    let facetUnchecked = [];
    let uncheckedFacetsJSX = null;

    keys.forEach(key => {
        if (counts[key] && counts[key] > 0) {
            const onClick = (event) => onClickMaker(
                "cadence_cat",
                facetRestricts.cadence_cat === key ? null : key
            );
            timeResFacets.push(
                <div
                  className={`searchResultFacetItem ${facetRestricts.cadence_cat===key ? "searchResultFacetSelected" :""}`}
                  onClick={() => { HandleFacetClick("time-resolutions", key); onClick()}}>
                  <input type="checkbox" checked={facetRestricts.cadence_cat===key} readOnly />
                  <span className="facetLabel"> {key} ({counts[key]}) </span>
                </div>
            );
        }
    });

    if(timeResolutions) {
        for (let i = 0; i < timeResolutions.length; i += 2) {
            const name = timeResolutions[i];
            const count = timeResolutions[i + 1];
            if (count > 0) {
                facetUnchecked.push({ name, count });
            }
        }

        uncheckedFacetsJSX = facetUnchecked.map((m, idx) => {
            const onClick = (event) => onClickMaker(
               "cadence_cat",
               facetRestricts.cadence_cat === m.name ? null : m.name
            );

           const fRArray = facetRestricts["cadence_cat"]?.split(",");

           const isSelected = fRArray?.filter((v,i)=>
                              {
                                  return v.startsWith(m.name)
                              });
           const isMissionRestricted = facetRestricts["mission_facet_cs"] ? true : false;

           return (
              <div
                key={idx}
                className={isSelected?.length > 0
                           ?  `searchResultFacetItem ${isSelected ? "searchResultFacetSelected":""}`
                           : isMissionRestricted
                               ? "searchResultUncheckedFacetItemBlue"
                               :"searchResultUncheckedFacetItem"}
                onClick={() => { HandleFacetClick("time-resolutions", m.name); onClick(); }}
              >
                <input
                  type="checkbox"
                  checked={isSelected?.length > 0}
                />
                <span className="facetLabel">{m.name} ({m.count})</span>
              </div>
            );
        })
    }

    const isEmptyObject = (obj) => {
        return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
    }

    if (timeResFacets.length === 0 && uncheckedFacetsJSX?.length === 0) {
        return <div/>;
    }

    return (
        <div className="searchResultsFacetSection">
           <p>
             {isEmptyObject(facetRestricts) && timeResFacets}
             {!isEmptyObject(facetRestricts) && uncheckedFacetsJSX}
           </p>
        </div>
    );
}


/**
 * Displays the section of the facet panel pertaining to Measurment Types
 */
function MeasurementTypeFacets(props) {
    const {facetData, facetRestricts, defaultFacetData, measurements, onClickMaker} = props;
    const measTypesFacets = [];
    const counts = getSolrListFacetCountAsObject(facetData?.facet_fields?.measurement_type_cs);
    const keys = Object.keys(counts).sort();
    const defaultNumberToShow = 5;
    const [measurementVisibleCount, setMeasurementVisibleCount] = useState(defaultNumberToShow);

    let facetUnchecked = [];
    let uncheckedFacetsJSX = null;

    let showMoreDisplay = "";
    fontAwesomeLibrary.add(faCaretDown);

    if(keys.length > 0) {
        keys.slice(0, measurementVisibleCount).forEach(key => {
            if (counts[key] && counts[key] > 0) {
                const onClick = (event) => onClickMaker(
                    "measurement_type_cs",
                    facetRestricts.measurement_type_cs === key ? null : key
                );
                measTypesFacets.push(
                    <div
                      className={`searchResultFacetItem ${facetRestricts.measurement_type_cs===key ? "searchResultFacetSelected":""}`}
                      onClick={() => { HandleFacetClick("measurement-types", key); onClick()}}>
                      <input
                        type="checkbox"
                        checked={facetRestricts.measurement_type_cs===key} />
                      <span className="facetLabel">{key} ({counts[key]})</span>
                    </div>
                );
            }
        });

       showMoreDisplay = keys.length < measurementVisibleCount ? "none" : "";
    }

    if(measurements) {
        for (let i = 0; i < measurements.length; i += 2) {
            const name = measurements[i];
            const count = measurements[i + 1];
            if (count > 0) {
                facetUnchecked.push({ name, count });
            }
        }

        uncheckedFacetsJSX = facetUnchecked.map((m, idx) => {
            const onClick = (event) => onClickMaker(
               "measurement_type_cs",
               facetRestricts.measurement_type_cs === m.name ? null : m.name
            );

           const fRArray = facetRestricts["measurement_type_cs"]?.split(",");

           const isSelected = fRArray?.filter((v,i)=>
                              {
                                  return v.startsWith(m.name)
                              });
           const isMissionRestricted = facetRestricts["mission_facet_cs"] ? true : false;

           return (
              <div
                key={idx}
                className={isSelected?.length > 0
                           ?  `searchResultFacetItem ${isSelected ? "searchResultFacetSelected":""}`
                           : isMissionRestricted
                               ? "searchResultUncheckedFacetItemBlue"
                               :"searchResultUncheckedFacetItem"}
                onClick={() => { HandleFacetClick("measurement-types", m.name); onClick(); }}
              >
                <input
                  type="checkbox"
                  checked={isSelected?.length > 0}
                />
                <span className="facetLabel">{m.name} ({m.count})</span>
              </div>
            );
        })

        showMoreDisplay = uncheckedFacetsJSX.length < measurementVisibleCount ? "none" : "";
    }


    const isEmptyObject = (obj) => {
        return obj && Object.keys(obj)?.length === 0 && obj.constructor === Object;
    }

    if (measTypesFacets?.length === 0 && uncheckedFacetsJSX?.length === 0) {
        return <div/>;
    }

    return (
        <div className="searchResultsFacetSection">
           <p>
             {isEmptyObject(facetRestricts) && measTypesFacets}
             {!isEmptyObject(facetRestricts) && uncheckedFacetsJSX?.slice(0, measurementVisibleCount)}
             <span
               onClick={() => setMeasurementVisibleCount(
                   measurementVisibleCount * 2
               )}
               style={{
                   color: "#2290BF",
                   display: showMoreDisplay,
                 cursor: "pointer"}}>
               <FontAwesomeIcon icon={faCaretDown} />
               &nbsp;
               See More
             </span>
           </p>
        </div>
    );
}


/**
 * Displays an expandable facet panel based on an arbitrary key.

 * @param props.key Facet key
 * @param props.title Title of the facet section
 * @param props.facetData Backend information about facets
 * @param props.facetRestricts Current facet restrictions in place on the page
 * @param props.onClickMaker Function to help alter the current facet
 * restrictions
 */
function ExpandableFacets(props) {
    const {facetKey, facetData, defaultFacetData, instruments, facetRestricts, sortOrder, onClickMaker} = props;
    const counts = facetData ? getSolrListFacetCountAsObject(facetData?.facet_fields[facetKey]) : 0;
    const defaultNumberToShow = 7;
    const [visibleCount, setVisibleCount] = useState(defaultNumberToShow);
    const facets = [];
    let sortFunc;


    if (sortOrder === "alpha") {
        // Sort alphabetical order
        sortFunc = (a, b) => a.localeCompare(b);
    } else {
        // Sort by counts
        sortFunc = (a, b) => counts[b] - counts[a];
    }

    let facetKeys = (
        Object
            .keys(counts)
            .filter(facetKey => counts[facetKey] && counts[facetKey] > 0)
            .sort(sortFunc)
    );

    facetKeys = facetKeys.slice(0, visibleCount);

    facetKeys.forEach(facetValue => {
        const onClick = (event) => onClickMaker(
            facetKey,
            facetRestricts[facetKey] === facetValue ? null : facetValue
        );
        const isSelected = facetRestricts[facetKey]?.split(",").includes(facetValue) ? true: false;

        facets.push(
            <div
              className={`searchResultFacetItem ${facetRestricts[facetKey]?.split(",").includes(facetValue) ? "searchResultFacetSelected":""}`}
              onClick={() => { HandleFacetClick(facetKey, facetValue); onClick()}}>
              <input
                type="checkbox"
                checked={isSelected} />
              <span className="facetLabel">{facetValue} ({counts[facetValue]})</span>
            </div>
        );
    });

    const isEmptyObject = (obj) => {
        return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
    }

    let facetUnchecked = [];
    let uncheckedFacetsJSX = null;
    let isMissionFacet = facetKey === "mission_facet_cs";
    let isInstrumentFacet = facetKey === "instrument_facet_cs";
    let atLeastOneSelected = false;

    let showMoreDisplay = facetKeys.length < visibleCount ? "none" : "";
    fontAwesomeLibrary.add(faCaretDown);

    // Handle Mission facet
    if(isMissionFacet && defaultFacetData) {
        for (let i = 0; i < defaultFacetData['facet_fields'][facetKey].length; i += 2) {
            const name = defaultFacetData['facet_fields'][facetKey][i];
            const count = defaultFacetData['facet_fields'][facetKey][i + 1];
            if (count > 0) {
                facetUnchecked.push({ name, count });
            }
        }

        uncheckedFacetsJSX = facetUnchecked.map((m, idx) => {
            const onClick = (event) => onClickMaker(
                 facetKey,
                 facetRestricts[facetKey] === m.name ? null : m.name
             );

            const fRArray = facetRestricts[facetKey]?.split(",");

            const isSelected = fRArray?.filter((v,i)=>
                              {
                                  return v.startsWith(m.name)
                              });
            atLeastOneSelected = isSelected ? true : atLeastOneSelected;
            let onlySubMissionsSelected = fRArray?.filter((v, i)=>{
                                               return v === m.name;
                                           }).length > 0 ? false : true;
            return (
               <div
                 key={idx}
                 className={isSelected?.length > 0 ?  "searchResultFacetItem searchResultFacetSelected" : "searchResultUncheckedFacetItem"}
                 onClick={() => { HandleFacetClick(facetKey, m.name); onClick(); }}
               >
                 <input
                   type="checkbox"
                   checked={isSelected?.length > 0 && !onlySubMissionsSelected}
                 />
                 <span className="facetLabel">{m.name} ({m.count})</span>
               </div>
            );
        })

        showMoreDisplay = defaultFacetData && defaultFacetData['facet_fields'][facetKey].length < visibleCount ? "none" : "";
    }

    // Handle Instruments Facet
    if(isInstrumentFacet && instruments) {
        for (let i = 0; i < instruments.length; i += 2) {
            const name = instruments[i];
            const count = instruments[i + 1];
            facetUnchecked.push({ name, count });
        }

        uncheckedFacetsJSX = facetUnchecked.map((m, idx) => {
            const onClick = (event) => onClickMaker(
               facetKey,
               facetRestricts[facetKey] === m.name ? null : m.name
            );

           const fRArray = facetRestricts[facetKey]?.split(",");

           const isSelected = fRArray?.filter((v,i)=>
                              {
                                  return v.startsWith(m.name)
                              });
           const isMissionRestricted = facetRestricts["mission_facet_cs"] ? true : false;

           return (
              <div
                key={idx}
                className={isSelected?.length > 0
                           ?  `searchResultFacetItem ${facetRestricts[facetKey]?.split(",").includes(m.name) ? "searchResultFacetSelected":""}`
                           : isMissionRestricted
                               ? "searchResultUncheckedFacetItemBlue"
                               :"searchResultUncheckedFacetItem"}
                onClick={() => { HandleFacetClick(facetKey, m.name); onClick(); }}
              >
                <input
                  type="checkbox"
                  checked={facetRestricts[facetKey]?.split(",").includes(m.name) ? true : false}
                />
                <span className="facetLabel" >{m.name} ({m.count !== 0 ? m.count : "---"})</span>
              </div>
            );
        })

        showMoreDisplay = uncheckedFacetsJSX.length < visibleCount ? "none" : "";
    }

    if (facets.length === 0) {
        return <div/>;
    }

    return (
        <div className="searchResultsFacetSection">
           <p>
             {(isEmptyObject(facetRestricts) || !["mission_facet_cs","instrument_facet_cs"].includes(facetKey)) && facets}
             {!isEmptyObject(facetRestricts) && uncheckedFacetsJSX?.slice(0, visibleCount)}
             <span
               onClick={() => setVisibleCount(
                   visibleCount * 2
               )}
               style={{
                   color: "#2290BF",
                   display: showMoreDisplay,
                 cursor: "pointer"}}>
               <FontAwesomeIcon icon={faCaretDown} />
               &nbsp;
               See More
             </span>
           </p>
        </div>
    );
}


/**
 * Handles the click event for a specific facet and facet item, sending event data to Google Analytics.
 *
 * @param facet String The name of the facet that was clicked
 * @param facetItem String The specific facet item that was selected
 */
function HandleFacetClick(facet, facetItem) {
    sendEventToGA(`search_results_page__facet__${facet}__button`, 'facets' , facet + "-" +facetItem);
}

/**
 * Displays the section of the facet panel pertaining to Era (decade)
 *
 * @param props.facetData Backend information about facets
 * @param props.facetRestricts Current facet restrictions in place on the page
 * @param props.onClickMaker Function to help alter the current facet
 * restrictions
 */
function EraFacets(props) {
    const {facetData, facetRestricts, eras, onClickMaker} = props;
    const counts = getSolrListFacetCountAsObject(facetData?.facet_fields?.eras);
    const keys = Object.keys(counts).sort().reverse();
    const erasFacets = [];

    let facetUnchecked = [];
    let uncheckedFacetsJSX = null;

    keys.forEach(key => {
        if (counts[key] && counts[key] > 0) {
            const onClick = (event) => onClickMaker(
                "eras",
                facetRestricts.eras === key ? null : key
            );
            erasFacets.push(
                <div
                  className={`searchResultFacetItem ${facetRestricts.eras===key ? "searchResultFacetSelected":""}`}
                  onClick={() => { HandleFacetClick("eras", key); onClick()}}>
                  <input
                    type="checkbox"
                    checked={facetRestricts.eras===key} />
                    <span className="facetLabel" >{key === "Future" ? key : `${key}s`} ({counts[key]})</span>
                </div>
            );
        }
    });


    if(eras) {
        for (let i = 0; i < eras.length; i += 2) {
            const name = eras[i];
            const count = eras[i + 1];
            if (count > 0) {
                facetUnchecked.push({ name, count });
            }
        }

        uncheckedFacetsJSX = facetUnchecked.map((m, idx) => {
            const onClick = (event) => onClickMaker(
               "eras",
               facetRestricts.eras === m.name ? null : m.name
            );

           const fRArray = facetRestricts["eras"]?.split(",");

           const isSelected = fRArray?.filter((v,i)=>
                              {
                                  return v.startsWith(m.name)
                              });
           const isMissionRestricted = facetRestricts["mission_facet_cs"] ? true : false;

           return (
              <div
                key={idx}
                className={isSelected?.length > 0
                           ?  `searchResultFacetItem ${isSelected?.length > 0 ? "searchResultFacetSelected":""}`
                           : isMissionRestricted
                               ? "searchResultUncheckedFacetItemBlue"
                               :"searchResultUncheckedFacetItem"}
                onClick={() => { HandleFacetClick("eras", m.name); onClick(); }}
              >
                <input
                  type="checkbox"
                  checked={isSelected?.length > 0}
                />
                <span className="facetLabel" >{m.name} ({m.count})</span>
              </div>
            );
        })
    }

    const isEmptyObject = (obj) => {
        return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
    }

    if (erasFacets.length === 0 && uncheckedFacetsJSX?.length === 0) {
        return <div/>;
    }

    return (
        <div className="searchResultsFacetSection">
           <p>
             {isEmptyObject(facetRestricts) && erasFacets}
             {!isEmptyObject(facetRestricts) && uncheckedFacetsJSX}
           </p>
        </div>
    );
}


/**
 * Displays the section of the facet panel relating to the Data Access Methods
 *
 * @param props.facetData Backend information about facets
 * @param props.facetRestricts Current facet restrictions in place on the page
 * @param props.onClickMaker Function to help alter the current facet
 * restrictions
 */
function AccessMethodFacets(props) {
    const {facetData, facetRestricts, onClickMaker} = props;
    const accessMethodFacets = [];

    const facetItems = [
        {
            name: "HTTPS",
            key: "has_https",
        },
        {
            name: "API",
            key: "has_api",
        },
        {
            name: "FTP",
            key: "has_ftp",
        },
        {
            name: "HELIOCLOUD S3",
            key: "has_heliocloud_s3",
        },
    ];

    const isMissionRestricted = facetRestricts["mission_facet_cs"] ? true : false;

    facetItems.forEach(({name, key}) => {
        if (facetData.facet_fields[key]) {
            const count = getSolrListFacetCountAsObject(facetData?.facet_fields[key])[true];
            const onClick = (event) => onClickMaker(key, !facetRestricts[key] || null);

            if (count > 0) {
                accessMethodFacets.push(
                    <div
                      className={isMissionRestricted
                                 ? "searchResultUncheckedFacetItemBlue"
                                 :"searchResultFacetItem"}
                      onClick={() => { HandleFacetClick("access-methods", name); onClick()}}>
                      <input type="checkbox" checked={facetRestricts[key]} />
                      <span className="facetLabel" >{name} ({count})</span>
                    </div>
                );
            }
        }
    });

    if (accessMethodFacets.length === 0) {
        return <div/>;
    }

    return (
        <div className="searchResultsFacetSectionLast">
           <p>
             {accessMethodFacets}
           </p>
        </div>
    );
}

